import {downloadCSVFiles, getDateWithTimeString, downloadCsvFromCdnUrl} from '@/helpers/shadcn/utils'
import {ComboProductListColConfig} from '@/services/MockData/Products/ComboProductListColConfig'
import {CountryProductListColConfig} from '@/services/MockData/Products/ProductListColConfig'
import {getInventoryFileUploadErrors} from '@/services/ProductsService/Fulfillment/inventory'
import {getProductCsvFile, getProductList} from '@/services/ProductsService/product'
import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {useQuery} from '@tanstack/react-query'
import {useMemo, useState} from 'react'
import {useSelector} from 'react-redux'
import {useDataTable} from './useDataTable-hook'

export const useProductList = (initialActiveTabIndex = 0, productTabFilters = []) => {
  const countryCode = useSelector(selectCountryCode)
  const {
    setSortingObj,
    setSearchArry,
    pageIndex,
    currentPage,
    sortingObj,
    searchArr,
    onPageIndexChange,
    createSearchArray,
    resetAllBtnClicked,
  } = useDataTable()

  const [btnDisabled, setBtnDisabled] = useState(false)
  const [activeTabIndex, setActiveTabIndex] = useState(initialActiveTabIndex)

  const queryParams = useMemo(() => {
    const params = new URLSearchParams()

    // Use provided sort, or fallback to default
    if (sortingObj?.column) {
      params.append('sortBy', sortingObj.column)
      params.append('order', sortingObj.direction)
    } else {
      params.append('sortBy', 'productId')
      params.append('order', 'DESC')
    }

    // Always include pagination
    params.append('page', pageIndex)
    params.append('pageSize', DEFAULT_TABLE_PAGE_SIZE)

    return params.toString()
  }, [sortingObj?.column, sortingObj?.direction, pageIndex])

  const getComboQueryData = useMemo(() => {
    const params = new URLSearchParams()

    // Use provided sort, or fallback to default
    if (sortingObj?.column) {
      params.append('sortBy', sortingObj.column)
      params.append('order', sortingObj.direction)
    } else {
      // params.append('sortBy', 'productId')
      params.append('order', 'DESC')
    }

    // Always include pagination
    params.append('page', pageIndex)
    params.append('pageSize', DEFAULT_TABLE_PAGE_SIZE)

    return params.toString()
  }, [sortingObj?.column, sortingObj?.direction, pageIndex])

  const prodSearchArr = [
    ...(searchArr || []),
    {
      field: 'isCombo',
      value: 'false',
      type: 'string',
    },
    ...productTabFilters,
  ]

  const productResults = useQuery({
    queryKey: ['PRODUCT_LIST', queryParams, prodSearchArr],
    queryFn: () => getProductList(prodSearchArr, queryParams),
    enabled: activeTabIndex === 0,
  })

  const comboSearchArr = [
    ...(searchArr || []),
    {
      field: 'isCombo',
      value: 'true',
      type: 'string',
    },
  ]

  const comboQueryResult = useQuery({
    queryKey: ['COMBO_PRODUCT_LIST', getComboQueryData, comboSearchArr],
    queryFn: () => getProductList(comboSearchArr, getComboQueryData),
    enabled: activeTabIndex === 1,
  })

  const onDownloadBtnClick = async (selectedIds, selectedColumns) => {
    if (btnDisabled) {
      return
    }
    setBtnDisabled(true)
    const defaultSortingObj =
      Object.keys(queryParams)?.length > 0
        ? `&${queryParams}`
        : `sortBy=productId&order=DESC&page=${pageIndex}&pageSize=10`
    const body = {
      ids: selectedIds,
      filters: prodSearchArr,
      ...(selectedColumns && { columns: selectedColumns }),
    }
    const data = await getProductCsvFile(body, defaultSortingObj)
    const url = data?.cdnUrl

    if (url) {
      downloadCsvFromCdnUrl(url)
      return
    }

    const fileName = `ProductList_${getDateWithTimeString()}`
    downloadCSVFiles(data, fileName)
    setBtnDisabled(false)
  }

  const productListData = useMemo(() => {
    const response = productResults?.data || {}

    if (!response?.data) return response

    const transformedData = response.data.map(item => {
      const subEn = item?.subCategories_en || ''

      const subEnCount =
        typeof subEn === 'string'
          ? subEn
              .split(',')
              .map(str => str.trim())
              .filter(Boolean).length
          : 0

      return {
        ...item,
        subCategories_en: subEnCount,
      }
    })

    return {
      ...response,
      data: transformedData,
    }
  }, [productResults?.data])

  const comboProductListData = useMemo(() => {
    const response = comboQueryResult?.data || {}

    if (!response?.data) return response

    return {
      ...response,
    }
  }, [comboQueryResult?.data])

  const fetchUploadErrors = async parentId => {
    const res = await getInventoryFileUploadErrors(parentId)

    const productRows = res?.rows || res?.data || []

    const flattened = productRows.flatMap(row => {
      const pid = row?.productId ?? '-'
      const errorsArr = row?.errors || []

      // If no errors, return the default object
      if (!Array.isArray(errorsArr) || errorsArr.length === 0) {
        return [
          {
            productId: pid,
            errorCol: '',
          },
        ]
      }

      const combinedErrorMsg = errorsArr.map(errItem => `${errItem?.errorCol} - ${errItem?.errorMsg}`).join(' , ')

      return [
        {
          productId: pid,
          errorCol: combinedErrorMsg,
        },
      ]
    })
    return flattened
  }

  return {
    productListData,
    productListColConfigData: CountryProductListColConfig(countryCode),
     comboProductListColConfigData: ComboProductListColConfig,
    currentPage,
    setSortingObj,
    setSearchArry,
    searchArr,
    onDownloadBtnClick,
    onPageIndexChange,
    isLoading: productResults?.isLoading || false,
    pageIndex,
    sortingObj,
    createSearchArray,
    resetAllBtnClicked,
    fetchUploadErrors,
    setActiveTabIndex,
    activeTabIndex,
    comboQueryResult,
    isComboLoading: comboQueryResult?.isLoading || false,
    comboProductListData,
  }
}
